/**
 * Appearance: theme + light/dark mode.
 *
 * Two orthogonal axes:
 *   - theme: `default | warm | forest | ocean` (palette family)
 *   - mode:  `light  | dark  | system`          (light/dark toggle)
 *
 * Applied as `<html data-theme="..." data-mode="dark">`. `data-mode` is
 * omitted for light. `system` reads `prefers-color-scheme` and listens for
 * changes so OS flips propagate live.
 *
 * Activation precedence:
 *   1. `?theme=` / `?mode=` URL params (session-only; not persisted server-side).
 *   2. `localStorage.soundcheckTheme` / `localStorage.soundcheckMode`.
 *   3. Defaults (`default` / `system`).
 *
 * Server sync: authenticated users reconcile from `me.appearance` (see
 * App.jsx). Setting a value writes localStorage + fires `updateAppearance`
 * optimistically; failures don't roll back the UI.
 */

const THEME_STORAGE_KEY = 'soundcheckTheme'
const MODE_STORAGE_KEY = 'soundcheckMode'
const VALID_THEMES = new Set(['default', 'warm', 'forest', 'ocean'])
const VALID_MODES = new Set(['light', 'dark', 'system'])

let _systemListener = null // singleton matchMedia listener
let _remoteSync = null // set by setRemoteSync(fn) to push changes server-side

export function setRemoteSync(fn) { _remoteSync = typeof fn === 'function' ? fn : null }

function _readStorage(key) {
  try { return localStorage.getItem(key) } catch { return null }
}
function _writeStorage(key, value) {
  try { localStorage.setItem(key, value) } catch { /* ignore */ }
}

function _consumeQueryParam(key, validSet) {
  if (typeof window === 'undefined') return null
  const value = new URLSearchParams(window.location.search).get(key)
  if (value && validSet.has(value)) {
    _writeStorage(key === 'theme' ? THEME_STORAGE_KEY : MODE_STORAGE_KEY, value)
    return value
  }
  return null
}

// ── Theme ────────────────────────────────────────────────────────────────

export function applyTheme() {
  if (typeof document === 'undefined') return
  const fromQuery = _consumeQueryParam('theme', VALID_THEMES)
  const stored = fromQuery || _readStorage(THEME_STORAGE_KEY)
  if (stored && VALID_THEMES.has(stored) && stored !== 'default') {
    document.documentElement.dataset.theme = stored
  } else {
    delete document.documentElement.dataset.theme
  }
}

export function setTheme(theme, { sync = true } = {}) {
  if (!VALID_THEMES.has(theme)) return
  _writeStorage(THEME_STORAGE_KEY, theme)
  applyTheme()
  if (sync && _remoteSync) {
    try { _remoteSync({ theme: theme === 'default' ? null : theme, mode: getMode() }) } catch { /* ignore */ }
  }
}

export function getTheme() {
  if (typeof document === 'undefined') return 'default'
  return document.documentElement.dataset.theme || 'default'
}

// ── Mode ─────────────────────────────────────────────────────────────────

function _matchDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function _setModeAttr(isDark) {
  if (isDark) document.documentElement.dataset.mode = 'dark'
  else delete document.documentElement.dataset.mode
}

export function applyMode() {
  if (typeof document === 'undefined') return
  const fromQuery = _consumeQueryParam('mode', VALID_MODES)
  const stored = fromQuery || _readStorage(MODE_STORAGE_KEY)
  const effective = VALID_MODES.has(stored) ? stored : 'system'

  // Tear down any previous system listener before re-attaching.
  if (_systemListener && window.matchMedia) {
    try { window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', _systemListener) } catch { /* ignore */ }
    _systemListener = null
  }

  if (effective === 'light') {
    _setModeAttr(false)
  } else if (effective === 'dark') {
    _setModeAttr(true)
  } else {
    _setModeAttr(_matchDark())
    if (window.matchMedia) {
      _systemListener = (e) => _setModeAttr(e.matches)
      try { window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', _systemListener) } catch { /* ignore */ }
    }
  }
}

export function setMode(mode, { sync = true } = {}) {
  if (!VALID_MODES.has(mode)) return
  _writeStorage(MODE_STORAGE_KEY, mode)
  applyMode()
  if (sync && _remoteSync) {
    const theme = getTheme()
    try { _remoteSync({ theme: theme === 'default' ? null : theme, mode }) } catch { /* ignore */ }
  }
}

export function getMode() {
  return _readStorage(MODE_STORAGE_KEY) || 'system'
}

/**
 * Reconcile local appearance with server values (called after getMe).
 * Server values win; written to localStorage and applied immediately.
 */
export function reconcileAppearance({ theme, mode }) {
  if (mode && VALID_MODES.has(mode)) {
    _writeStorage(MODE_STORAGE_KEY, mode)
  }
  // Server stores `theme=null` for default; normalise.
  const nextTheme = theme && VALID_THEMES.has(theme) ? theme : 'default'
  _writeStorage(THEME_STORAGE_KEY, nextTheme)
  applyTheme()
  applyMode()
}
